import { Anchor, Group, Paper, Stack, Text, Title } from "@mantine/core";
import type { ComponentProps } from "react";
import { AppWebviewLayout } from "./AppWebviewLayout";
import { AppWebviewButton } from "./AppWebviewButton";
import classes from "./IntroScreen.module.css";

export interface IntroStep {
  icon: string;
  title: string;
  description: string;
}

interface IntroScreenProps {
  title: string;
  intro?: string;
  steps: IntroStep[];
  ctaLabel: string;
  ctaProps?: ComponentProps<typeof AppWebviewButton>;
  onContinue: () => void;
}

const legalLinks: { label: string; path: string }[] = [
  {
    label: "Behandling av personuppgifter",
    path: "/behandling-av-personuppgifter",
  },
  { label: "Allmänna villkor", path: "/villkor" },
];

export function IntroScreen({
  title,
  intro,
  steps,
  ctaLabel,
  ctaProps,
  onContinue,
}: IntroScreenProps) {
  return (
    <AppWebviewLayout
      footer={
        <>
          <AppWebviewButton size="lg" onClick={onContinue} {...ctaProps}>
            {ctaLabel}
          </AppWebviewButton>
          <Group justify="space-between" gap="xs" wrap="nowrap" mt="xs">
            {legalLinks.map(({ label, path }) => (
              <Anchor
                key={path}
                href={`${import.meta.env.VITE_LANDING_URL}${path}`}
                target="_blank"
                rel="noopener noreferrer"
                size="sm"
                c="dimmed"
                underline="never"
              >
                {label}
              </Anchor>
            ))}
          </Group>
        </>
      }
    >
      <Stack gap="lg">
        <Stack gap="xs">
          <Title
            order={1}
            fz="h2"
            lh="h2"
            ff="var(--wallet-font-family-sambla-sans)"
            ta="center"
          >
            {title}
          </Title>
          {intro && (
            <Text size="sm" c="dimmed" ta="center" lh={1.5}>
              {intro}
            </Text>
          )}
        </Stack>

        <Stack gap="md">
          {steps.map(({ icon, title: stepTitle, description }) => (
            <Paper
              key={stepTitle}
              radius="lg"
              p="md"
              className={classes.stepCard}
            >
              <Stack gap="xs">
                <Group gap="sm" align="center">
                  <img src={icon} alt="" width={24} height={24} />
                  <Title order={2} fz="md" lh="md" fw={600}>
                    {stepTitle}
                  </Title>
                </Group>
                <Text size="sm" c="dimmed" lh={1.5}>
                  {description}
                </Text>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Stack>
    </AppWebviewLayout>
  );
}
